import { useMemo, useCallback } from 'react'
import type {
  ProjectLabel,
  MindMapNode,
  MindMapEdge,
  StakeholderRatings,
  MindMapNodeData,
} from '@bandos/shared'

export function useNodeUtils(
  nodes: MindMapNode[],
  edges: MindMapEdge[],
  projectLabels: ProjectLabel[],
  nodeLabelMap: Map<string, string[]>,
  effectiveShouldDisplayNode: (nodeId: string) => boolean
) {
  // Helper to get effective node type (handles summarized/collapsed nodes)
  const getEffectiveNodeType = useCallback((node: MindMapNode): string => {
    if (node.type === 'summarizedNode') {
      const data = node.data as MindMapNodeData & {
        originalNodeType?: string
        originalNodeData?: MindMapNodeData
      }
      return data.originalNodeType ?? node.type
    }
    return node.type || ''
  }, [])

  // Helper to get effective node data (handles summarized/collapsed nodes)
  const getEffectiveNodeData = useCallback(
    (node: MindMapNode): MindMapNodeData & { stakeholderRating?: StakeholderRatings } => {
      if (node.type === 'summarizedNode') {
        const data = node.data as MindMapNodeData & {
          originalNodeType?: string
          originalNodeData?: MindMapNodeData
        }
        return (data.originalNodeData ?? node.data) as MindMapNodeData & {
          stakeholderRating?: StakeholderRatings
        }
      }
      return node.data as MindMapNodeData & { stakeholderRating?: StakeholderRatings }
    },
    []
  )

  // Build node map for quick lookups
  const nodeMap = useMemo(() => {
    return new Map(nodes.map((n) => [n.id, n]))
  }, [nodes])

  // Get child ideas for shared components, sorted by visual weight asc (solo nodes first)
  const getChildIdeas = useCallback(
    (parentId: string): MindMapNode[] => {
      if (nodes.length === 0) return []
      const childEdges = edges.filter((edge) => edge.source === parentId)
      const childNodes = childEdges
        .map((edge) => nodeMap.get(edge.target))
        .filter((node): node is MindMapNode => node !== undefined)
        .filter((node) => effectiveShouldDisplayNode(node.id))
        .filter(
          (node) =>
            node.type === 'ideaNode' ||
            (node.type === 'summarizedNode' && getEffectiveNodeType(node) === 'ideaNode')
        )
      const unique = Array.from(new Map(childNodes.map((node) => [node.id, node])).values())
      // Recursive visual weight: each idea child = 1, storyboard/survey = 10 (takes much more space)
      const nodeScore = (id: string, visited = new Set<string>()): number => {
        if (visited.has(id)) return 0
        const next = new Set(visited).add(id)
        return edges
          .filter((e) => e.source === id)
          .reduce((sum, e) => {
            const n = nodeMap.get(e.target)
            if (!n) return sum
            const effectiveType = n.type === 'summarizedNode' ? getEffectiveNodeType(n) : n.type
            if (effectiveType === 'ideaNode') return sum + 1 + nodeScore(n.id, next)
            if (effectiveType === 'storyboardNode') return sum + 10
            return sum
          }, 0)
      }
      return unique.sort((a, b) => nodeScore(a.id) - nodeScore(b.id))
    },
    [nodes, edges, nodeMap, effectiveShouldDisplayNode, getEffectiveNodeType]
  )

  // Get labels for a node (for shared components)
  const getLabelsForNode = useCallback(
    (nodeId: string): ProjectLabel[] => {
      const labelIds = nodeLabelMap.get(nodeId) ?? []
      return labelIds
        .map((id) => projectLabels.find((l) => l.id === id))
        .filter((l): l is ProjectLabel => l !== undefined)
    },
    [nodeLabelMap, projectLabels]
  )

  return {
    getEffectiveNodeType,
    getEffectiveNodeData,
    nodeMap,
    getChildIdeas,
    getLabelsForNode,
  }
}
